import React from 'react';
import { FaExclamationCircle } from 'react-icons/fa';
import PlayerAvatar from '../common/PlayerAvatar';
import { useAppContext } from '../../hooks/useAppContext';
import type { ClientChatMessage } from '../../types/chat.types';
import { linkify } from '../../utils/linkify';

interface MessageItemProps {
  message: ClientChatMessage;
  isGrouped?: boolean;
  /** Send one of our failed messages again. */
  onRetry?: (messageId: string) => void;
}

export const MessageItem: React.FC<MessageItemProps> = ({ message, isGrouped = false, onRetry }) => {
  const { auth } = useAppContext();
  const isOwnMessage = message.userId === auth.user?.id;
  const isSending = message.status === 'sending';
  const isFailed = message.status === 'failed';

  // Format timestamp
  const formatTime = (timestamp: string) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return 'now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    
    return date.toLocaleDateString();
  };

  // Process content for links, then @mentions within the remaining text
  const processContent = (content: string) => {
    return linkify(content).map((segment, index) => {
      if (segment.type === 'link') {
        return (
          <a
            key={`link-${index}`}
            href={segment.href}
            target="_blank"
            rel="noopener noreferrer"
            className="text-game-accent underline hover:opacity-80 break-all"
          >
            {segment.value}
          </a>
        );
      }
      return <React.Fragment key={`text-${index}`}>{processMentions(segment.value)}</React.Fragment>;
    });
  };

  // Process content for @mentions
  const processMentions = (content: string) => {
    const mentionRegex = /@(\w+)/g;
    const parts = content.split(mentionRegex);
    
    return parts.map((part, index) => {
      // Check if this part is a username (odd indices after split)
      if (index % 2 === 1) {
        const isSelfMention = part === auth.user?.username;
        return (
          <span
            key={index}
            className={`font-semibold px-1 rounded ${
              isSelfMention 
                ? 'bg-game-golden/20 text-game-golden' 
                : 'bg-game-accent/20 text-game-accent'
            }`}
          >
            @{part}
          </span>
        );
      }
      return part;
    });
  };

  return (
    <div 
      className={`flex gap-3 px-2 py-1 hover:bg-gray-900/30 rounded group ${
        isSending ? 'opacity-70' : ''
      }`}
    >
      {!isGrouped && (
        <div className="flex-shrink-0">
          <PlayerAvatar 
            username={message.username} 
            avatar={message.avatar} 
            size="sm" 
          />
        </div>
      )}
      
      {isGrouped && <div className="w-8" />}
      
      <div className="flex-1 min-w-0">
        {!isGrouped && (
          <div className="flex items-baseline gap-2 mb-1">
            <span 
              className={`font-semibold text-sm ${
                isOwnMessage ? 'text-game-accent' : 'text-white'
              }`}
            >
              {message.username}
            </span>
            <span className="text-xs text-gray-400">
              {isSending ? 'Sending...' : isFailed ? 'Not sent' : formatTime(message.createdAt)}
            </span>
          </div>
        )}
        
        <div 
          className={`text-sm break-words ${
            isOwnMessage ? 'text-gray-200' : 'text-gray-200'
          } ${isSending ? 'italic' : ''}`}
        >
          {processContent(message.content)}
        </div>

        {/* Refused, or never answered: say so, and offer to send it again */}
        {isFailed && (
          <div className="flex items-center gap-2 mt-1 text-xs">
            <FaExclamationCircle className="text-game-orange" aria-hidden="true" />
            <span className="text-game-orange font-semibold">Message not sent.</span>
            {onRetry && (
              <button
                type="button"
                onClick={() => onRetry(message.id)}
                className="font-semibold text-game-golden hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-game-golden rounded"
              >
                Retry
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};